import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Breadcrumb,
  Button,
  Col,
  Row,
  Spin,
  Tabs,
  TabsProps,
  Typography,
} from 'antd';
import moment from 'moment';
import { useTypedFlags } from 'client/component-library/FeatureFlags/hooks/useTypedFlags';
import { TeamRoutes, itemRender } from 'client/modules/team/RouteHelpers';
import { TeamSearch } from 'client/modules/team/components/TeamSearch';
import useSelectedTab from 'client/modules/team/hooks/useSelectedTab';
import { useGetPurgeScheduleByUserIdQuery } from 'client/modules/team/services/hooks/useGetPurgeScheduleByUserIdQuery';
import { useGetUserInformation } from '../hooks/useGetUserInformation';
import UserDetail from '../components/UserDetail';
import PackageSettings from '../components/PackageSettings';
import Divider from '../components/Divider';
import { UserActions } from '../components/UserActions';
import UserHistory from '../components/UserHistory';
import { LEGACY_SITE } from '../constants';
import FeatureUsage from '../components/FeatureUsage';
import UserEvents from '../components/UserEvents';
import EmailEvents from '../components/EmailEvents';
import PersonalizationSettings from '../components/PersonalizationSettings';
import TransferWatchlists from '../components/TransferWatchlists';
import UserUpdates from '../components/UserUpdates';
import { useUserPermissions } from '../hooks/useUserPermissions';

const NewTabs = ({ items, activeKey, onChange }: TabsProps) => {
  return <Tabs activeKey={activeKey} items={items} onChange={onChange} />;
};

const UserDetailPage = () => {
  const { id, isLoading, user, userTrialInfo, refetchUserQuery } =
    useGetUserInformation();

  const { data: purgeSchedule } = useGetPurgeScheduleByUserIdQuery({
    enabled: Boolean(id),
    params: { idUser: id },
  });

  const { hasChangePackagePermission } = useUserPermissions();

  const TabKeys = {
    loginHistory: 'login-history',
    featureUsage: 'feature-usage',
    userEvents: 'userEvents',
    emailEvents: 'emailEvents',
    updates: 'updates',
    ownershipTransfer: 'ownership-transfer',
  };

  const items = [
    {
      key: TabKeys.loginHistory,
      label: <span className="text-xl">History</span>,
      children: <UserHistory />,
    },
    {
      key: TabKeys.featureUsage,
      label: <span className="text-xl">Feature Usage</span>,
      children: <FeatureUsage idUser={id} teamId={user?.team?.id} />,
    },
    {
      key: TabKeys.userEvents,
      label: <span className="text-xl">User Events</span>,
      children: <UserEvents idUser={id} enabled />,
    },
    {
      key: TabKeys.emailEvents,
      label: <span className="text-xl">Emails</span>,
      children: <EmailEvents idUser={id} enabled />,
    },
    {
      key: TabKeys.updates,
      label: <span className="text-xl">Updates</span>,
      children: <UserUpdates idUser={id} />,
    },
    ...(hasChangePackagePermission
      ? [
          {
            key: TabKeys.ownershipTransfer,
            label: <span className="text-xl">Ownership Transfer</span>,
            children: <TransferWatchlists idUser={id} />,
          },
        ]
      : []),
  ];

  const tabItemKeys = items.map((item) => item.key);

  const { defaultActiveKey, onChangeTab: syncTabToUrl } = useSelectedTab({
    tabItems: tabItemKeys,
  });

  const [selectedTab, setSelectedTab] = useState(defaultActiveKey);

  const activeKey = tabItemKeys.includes(selectedTab)
    ? selectedTab
    : tabItemKeys[0];

  useEffect(() => {
    if (selectedTab !== activeKey) {
      setSelectedTab(activeKey);
      syncTabToUrl(activeKey);
    }
  }, [selectedTab, activeKey, syncTabToUrl]);

  const handleTabChange = (newKey: string) => {
    setSelectedTab(newKey);
    syncTabToUrl(newKey);
  };

  const { getFlag } = useTypedFlags();

  const isLegacySiteEnabled = getFlag(LEGACY_SITE);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="pb-20">
      <Row className="mb-2">
        <Col
          xl={{ push: 2, span: 6 }}
          md={{ push: 2, span: 10 }}
          xs={{ push: 1, span: 22 }}
          className="flex items-center mt-[30px] pl-3"
        >
          {user?.first_name && (
            <Avatar>{`${user?.first_name['0']}${user?.last_name['0']}`}</Avatar>
          )}
          <span className="mb-0 ml-3 mr-2 text-3xl">{`${user?.first_name} ${user?.last_name}`}</span>
          <span className="text-sm">(user id: {id})</span>
        </Col>
        <Col
          xl={{ push: 8, span: 8 }}
          md={{ push: 4, span: 8 }}
          xs={{ push: 1, span: 22 }}
          className="flex justify-end items-end"
        >
          <TeamSearch />
        </Col>
      </Row>
      <Row className="mb-8">
        <Col
          xl={{ push: 2, span: 3 }}
          md={{ push: 2, span: 3 }}
          xs={{ push: 1, span: 22 }}
          className="flex items-center pl-3"
        >
          <Breadcrumb
            routes={[
              { breadcrumbName: 'Search', path: TeamRoutes.Search },
              { breadcrumbName: `User ${user.id_user}`, path: '' },
            ]}
            itemRender={itemRender}
          />
        </Col>
        <Col
          xl={{ push: 6, span: 13 }}
          md={{ push: 6, span: 13 }}
          xs={{ push: 1, span: 22 }}
          className="flex justify-end items-end"
        >
          <UserActions email={user.email} idUser={user.id_user} />
          {isLegacySiteEnabled && (
            <Button
              className="ml-2"
              type="dashed"
              href={`${window.envVars.LEGACY_DOMAIN}/admin/user/${id}`}
              target="_blank"
            >
              Use Legacy Site
            </Button>
          )}
        </Col>
      </Row>
      {purgeSchedule?.is_scheduled && (
        <Row>
          <Col
            xl={{ push: 2, span: 20 }}
            md={{ push: 2, span: 20 }}
            xs={{ push: 1, span: 22 }}
          >
            <Alert
              type="warning"
              showIcon
              message={`This user is scheduled to be purged on ${moment(
                purgeSchedule.purge_date
              ).format('MM/DD/YYYY')}.`}
              className="mb-4"
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col
          xl={{ push: 2, span: 7 }}
          md={{ push: 2, span: 20 }}
          xs={{ push: 1, span: 22 }}
        >
          <Typography.Title level={4} className="!font-medium mt-4">
            User Details
          </Typography.Title>
          <Divider className="border-t-2 border-black border-solid" />
          <UserDetail
            userId={user.id_user}
            email={user.email}
            company={user.company}
            isActive={user.active}
            firtsName={user.first_name}
            lastName={user.last_name}
            teamName={user.team?.name}
            teamId={user.team?.id}
            portal={user.id_portal}
            refetchUserQuery={refetchUserQuery}
            ts_created={user.ts_created}
          />
        </Col>
        <Col
          xl={{ push: 2, span: 6 }}
          md={{ push: 2, span: 20 }}
          xs={{ push: 1, span: 22 }}
        >
          <Typography.Title level={4} className="!font-medium mt-4">
            Package Settings
          </Typography.Title>
          <Divider className="border-t-2 border-black border-solid" />
          <PackageSettings
            refetchUserQuery={refetchUserQuery}
            packageId={user.package?.id}
            packageName={user.package?.name}
            packageEndDate={moment(user.package_end).format('MM/DD/YYYY')}
            packageStartDate={moment(user.package_start).format('MM/DD/YYYY')}
            trialInfo={userTrialInfo}
            userId={user.id_user}
          />
        </Col>
        <Col
          xl={{ push: 2, span: 7 }}
          md={{ push: 2, span: 20 }}
          xs={{ push: 1, span: 22 }}
        >
          <Typography.Title level={4} className="!font-medium mt-4">
            Personalization Settings
          </Typography.Title>
          <Divider className="border-t-2 border-black border-solid" />
          <PersonalizationSettings
            userId={user.id_user}
            refetchUserQuery={refetchUserQuery}
            linkedEntity={parseInt(user.id_cbi_entity as unknown as string, 10)}
          />
        </Col>
      </Row>
      <Row className="mb-5 mt-10">
        <Col sm={{ push: 1, span: 22 }} xl={{ push: 2, span: 20 }}>
          <NewTabs
            items={items}
            activeKey={activeKey}
            onChange={handleTabChange}
          />
        </Col>
      </Row>
    </div>
  );
};

export default UserDetailPage;
